/**
 * 화면 표시 폰트(개인 설정) — 카탈로그·적용·설치여부 판정.
 *
 * 배경(2026-08-20): 게이트웨이엔 웹폰트가 하나도 없었다. index.html 의
 * `body { font-family: -apple-system, sans-serif; }` 가 전부였고 `-apple-system` 은 맥 전용이라
 * 윈도우에선 제네릭 sans-serif 로 떨어졌다 → **직원 PC마다 다른 폰트로 보였다.**
 *
 * 🔴 화면 전용이다. 내보내기(PDF/DOCX)에는 적용하지 않는다 —
 *    브라우저 PDF 인쇄는 폰트를 파일에 서브셋 임베딩하므로 그건 '배포'가 되고,
 *    상용 폰트는 PDF 임베딩을 제한하는 EULA 가 있다.
 *    (useChatExport.jsx 의 savePdf / 서버 doc_export.py 는 고정 폰트를 계속 쓴다.)
 *
 * 번들 4종(+Pretendard)은 전부 OFL-1.1 이라 서버가 서빙해도 문제없다.
 * 'custom:<이름>' 은 **이름만** 싣는다 — 서버는 그 폰트 파일을 저장·전송하지 않고,
 * 그리는 주체는 그 PC의 브라우저다.
 */

// 폰트 파일은 public/fonts/ 에 있다(scripts/copy-fonts.js 가 node_modules 에서 생성).
// 🔴 webpack 으로 import 하지 않는다 — Pretendard dynamic-subset 을 import 했더니
//    폰트 파일 1,656개(28MB)가 번들에 딸려 들어와 build/ 가 69MB 가 됐다.
//    public/ 은 webpack 미경유라 nginx 가 정적으로 서빙하고, 고른 사람만 내려받는다.
export const CUSTOM_PREFIX = 'custom:';
export const DEFAULT_FONT = 'pretendard';
export const STORAGE_KEY = 'gwUiFont';

/** key 는 backend/services/ui_fonts.py 의 FONT_KEYS 와 **정확히 일치**해야 한다.
 *  (backend/tests/test_ui_fonts.py 가 두 파일을 대조해 드리프트를 막는다.) */
export const FONTS = [
  { key: 'pretendard',       label: '프리텐다드',        family: 'Pretendard',       note: '기본값 · 화면용으로 가장 무난' },
  { key: 'noto-sans-kr',     label: '본고딕',            family: 'Noto Sans KR',     note: '구글 본고딕 · 획이 또렷' },
  { key: 'nanum-gothic',     label: '나눔고딕',          family: 'Nanum Gothic',     note: '익숙한 표준 고딕' },
  { key: 'ibm-plex-sans-kr', label: 'IBM Plex Sans KR',  family: 'IBM Plex Sans KR', note: '살짝 좁아 정보 밀도가 높음' },
  { key: 'nanum-myeongjo',   label: '나눔명조',          family: 'Nanum Myeongjo',   note: '명조체 · 긴 글 읽기용' },
  { key: 'system',           label: '시스템 기본',       family: null,               note: '웹폰트를 받지 않음 · PC 설정 그대로' },
];

const SYSTEM_STACK = 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", "Malgun Gothic", sans-serif';
const FALLBACK = 'Pretendard, ' + SYSTEM_STACK;

/** 저장값 → CSS font-family 스택 */
export function cssStack(value) {
  const v = value || DEFAULT_FONT;
  if (v.startsWith(CUSTOM_PREFIX)) {
    const name = v.slice(CUSTOM_PREFIX.length).trim();
    // 서버(ui_fonts.normalize_font)가 따옴표·중괄호·세미콜론을 이미 막지만,
    // 프론트만 거쳐 들어오는 경로가 생겨도 안전하도록 여기서도 한 번 더 막는다.
    if (!name || /["'{};()\\<>]/.test(name)) return FALLBACK;
    return '"' + name + '", ' + FALLBACK;
  }
  if (v === 'system') return SYSTEM_STACK;
  const hit = FONTS.find((f) => f.key === v);
  if (!hit || !hit.family) return FALLBACK;
  return '"' + hit.family + '", ' + SYSTEM_STACK;
}

/**
 * 선택된 폰트의 CSS 를 <link> 로 붙인다. 기본값(pretendard)은 index.html 이 이미 걸어 뒀고,
 * 'system' 은 웹폰트가 없으며, 'custom:'은 그 PC 설치 폰트라 받을 게 없다.
 *
 * @font-face 는 지연 로딩이라 <link> 를 붙여도 그 폰트로 그리는 글자가 생겨야 실제로 내려받는다.
 */
const loaded = new Set(['pretendard', 'system']);
export function loadFontAssets(key) {
  if (!key || loaded.has(key) || key.startsWith(CUSTOM_PREFIX)) return;
  if (typeof document === 'undefined') return;
  loaded.add(key);
  const href = '/fonts/' + key + '.css';
  if (document.querySelector('link[href="' + href + '"]')) return;
  const el = document.createElement('link');
  el.rel = 'stylesheet';
  el.href = href;
  document.head.appendChild(el);
}

/** 문서 전체에 적용. 모든 화면이 var(--gw-font) 를 쓰므로 이 한 줄이 전역 반영이다. */
export function applyFont(value) {
  const v = value || DEFAULT_FONT;
  loadFontAssets(v);
  if (typeof document !== 'undefined') {
    document.documentElement.style.setProperty('--gw-font', cssStack(v));
  }
  return v;
}

/** 서버 응답을 기다리는 동안의 깜빡임(FOUT)을 막는 로컬 캐시 */
export function readStoredFont() {
  try { return localStorage.getItem(STORAGE_KEY) || null; } catch (e) { return null; }
}
export function storeFont(value) {
  try {
    if (value) localStorage.setItem(STORAGE_KEY, value);
    else localStorage.removeItem(STORAGE_KEY);
  } catch (e) { /* 사파리 프라이빗 등 — 저장 실패해도 동작엔 지장 없음 */ }
}

/** 서버가 내려준 사용자 객체(로그인 응답 / auth/me)의 ui_font 를 화면·캐시에 반영한다.
 *  user 에 ui_font 키 자체가 없으면(옛 응답 등) 아무것도 하지 않는다 — 캐시를 지우지 않는다. */
export function syncFontFromUser(user) {
  if (!user || !Object.prototype.hasOwnProperty.call(user, 'ui_font')) return;
  storeFont(user.ui_font);
  applyFont(user.ui_font);
}

/**
 * 그 PC에 폰트가 실제로 설치돼 있는지 판정한다(캔버스 폭 비교).
 *
 * document.fonts.check() 는 로컬 설치 폰트에 대해 신뢰할 수 없어서(대개 무조건 true)
 * 고전적인 방식을 쓴다: 기준 폰트 3종으로 같은 문자열을 그린 폭과, 후보 폰트를 앞세운 폭을 비교.
 * 하나라도 폭이 다르면 후보가 실제로 적용된 것 = 설치돼 있다.
 */
const BASES = ['monospace', 'serif', 'sans-serif'];
const PROBE = '한글ABCabc0123가나다라마바사';
let _ctx = null;
export function isFontInstalled(name) {
  if (!name || typeof document === 'undefined') return false;
  if (/["'{};()\\<>]/.test(name)) return false;
  try {
    if (!_ctx) _ctx = document.createElement('canvas').getContext('2d');
    if (!_ctx) return false;
    return BASES.some((base) => {
      _ctx.font = '48px ' + base;
      const baseWidth = _ctx.measureText(PROBE).width;
      _ctx.font = '48px "' + name + '", ' + base;
      return Math.abs(_ctx.measureText(PROBE).width - baseWidth) > 0.5;
    });
  } catch (e) {
    return false;
  }
}

/** 윈도우/맥에 흔한 한글 폰트 후보 — 실제 설치된 것만 골라 보여준다. */
export const COMMON_LOCAL_FONTS = [
  '맑은 고딕', 'Malgun Gothic', '나눔고딕', 'NanumGothic', '나눔바른고딕',
  '함초롬돋움', '함초롬바탕', '굴림', '돋움', '바탕', '궁서',
  'Noto Sans KR', 'Spoqa Han Sans Neo', 'KoPubWorld돋움체', 'KoPubWorld바탕체',
  '카카오 regular', 'Apple SD Gothic Neo', 'AppleGothic', 'Segoe UI', 'Arial',
];
export function detectLocalFonts() {
  const seen = new Set();
  return COMMON_LOCAL_FONTS.filter((n) => {
    const k = n.toLowerCase();
    if (seen.has(k)) return false;
    seen.add(k);
    return isFontInstalled(n);
  });
}
